"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Info, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface BathroomProject {
  id: string;
  title: string;
  location: string;
  tag: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  specifications: string;
}
const bathroomProjects: BathroomProject[] = [{
  id: "classic-marble-white-oak",
  title: "Classic Marble & White Oak Master Suite",
  location: "Brookhaven, GA",
  tag: "Calacatta Gold & White Oak",
  description: "Polished Calacatta marble floor-to-ceiling tiling, freestanding soaking tub, and custom double vanity in natural white oak.",
  specifications: "Mitered edge stone, dual unlacquered brass sconces",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/historic_brookhaven_master_suite.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/historic_brookhaven_master_suite.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/historic_brookhaven_master_suite.png-wvc-sizes",
  alt: "Classic master bathroom with soaking tub and white oak double vanity"
}, {
  id: "bookmatched-slab-walk-in",
  title: "Bookmatched Slab Walk-In Shower",
  location: "Brookhaven, GA",
  tag: "Bookmatched Carrara",
  description: "Floor-to-ceiling bookmatched Carrara marble slabs, unlacquered brass dual shower heads, and frameless zero-threshold glass enclosure.",
  specifications: "Linear drain trench, concealed thermostatic valve",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/capital_city_club_walk_in_shower.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/capital_city_club_walk_in_shower.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/capital_city_club_walk_in_shower.png-wvc-sizes",
  alt: "Walk-in shower with bookmatched Carrara marble slabs and brass fixtures"
}, {
  id: "honed-dolomite-guest-ensuite",
  title: "Honed Dolomite Guest Ensuite",
  location: "Brookhaven, GA",
  tag: "Herringbone Dolomite",
  description: "Herringbone honed dolomite marble floors, floating walnut vanity, and architecturally aligned matte black plumbing fixtures.",
  specifications: "Wall-hung storage, integrated backlit anti-fog mirror",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/peachtree_dunwoody_guest_ensuite.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/peachtree_dunwoody_guest_ensuite.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/peachtree_dunwoody_guest_ensuite.png-wvc-sizes",
  alt: "Modern guest ensuite bathroom with floating walnut vanity and herringbone tile"
}, {
  id: "atmospheric-steam-spa-retreat",
  title: "Atmospheric Steam & Spa Retreat",
  location: "Brookhaven, GA",
  tag: "Heated Mosaic & Steam",
  description: "Custom steam shower system with heated mosaic marble floors, floating bench, and recessed ambient niche lighting.",
  specifications: "Aromatherapy steam generator, seamless slab niche",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/ashford_park_spa_retreat.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/ashford_park_spa_retreat.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/ashford_park_spa_retreat.png-wvc-sizes",
  alt: "Spa retreat bathroom with custom steam shower and recessed ambient lighting"
}];
export default function BathroomGallery() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="bathroom-gallery" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3.5 py-1 text-xs font-semibold tracking-widest text-primary uppercase">
            <Sparkles className="size-3.5" />
            <span>Brookhaven Gallery</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mt-4 mb-3">
            Recent ensuite and bath transformations
          </h2>

          <div className="mx-auto my-4 h-[1px] w-24 bg-gradient-to-r from-transparent via-primary/60 to-transparent" />

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Step through master bath suites and walk-in showers built across Brookhaven neighborhoods.
          </p>
        </div>

        {/* Gallery Disclaimer Note */}
        <div className="mt-8 sm:mt-10 mx-auto max-w-3xl rounded-lg border border-border/80 bg-muted/60 px-4 py-3 text-center sm:px-6">
          <p className="text-xs sm:text-sm font-medium text-muted-foreground flex items-center justify-center gap-2">
            <Info className="size-4 shrink-0 text-primary" aria-hidden="true" />
            <span>
              These images are Remodeling Inspiration &amp; Design Concepts — they are not photographs of completed Brookhaven Elevation Renovations projects.
            </span>
          </p>
        </div>

        {/* 2x2 Gallery Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {bathroomProjects.map((project, index) => <motion.article key={project.id} data-index={index} variants={itemVariants} whileHover={shouldReduceMotion ? {} : {
          y: -4
        }} className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-primary/50 hover:shadow-md">
              {/* Image Container with 3:2 Aspect Ratio */}
              <div className="overflow-hidden bg-muted relative">
                <AspectRatio ratio={3 / 2}>
                  <img src={project.image} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
                </AspectRatio>
                <div className="absolute top-4 left-4 z-10">
                  <Badge variant="outline" className="border-white/40 bg-card/90 backdrop-blur-md text-foreground text-xs font-medium px-2.5 py-1 shadow-2xs">
                    {project.location}
                  </Badge>
                </div>
              </div>

              {/* Metadata Panel */}
              <div className="flex flex-1 flex-col justify-between p-6 sm:p-7">
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2.5">
                    <span className="text-xs font-medium tracking-wide text-primary uppercase">
                      {project.tag}
                    </span>
                  </div>

                  <h3 className="font-sans text-xl sm:text-2xl font-semibold text-foreground tracking-tight group-hover:text-primary transition-colors duration-200">
                    {project.title}
                  </h3>

                  <p className="mt-2.5 text-sm sm:text-base leading-relaxed text-muted-foreground">
                    {project.description}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-mono">{project.specifications}</span>
                  <Link to="/OurProjectsPage" className="inline-flex items-center gap-1 font-semibold text-foreground hover:text-primary transition-colors group/link">
                    <span>View project</span>
                    <ArrowRight className="size-3.5 transition-transform duration-200 group-hover/link:translate-x-0.5" />
                  </Link>
                </div>
              </div>
            </motion.article>)}
        </motion.div>

        {/* Section Action */}
        <div className="mt-14 sm:mt-16 text-center">
          <Button asChild size="lg" className="rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/90 px-8 py-3.5 shadow-sm transition-[transform,background-color] hover:-translate-y-0.5">
            <Link to="/OurProjectsPage" className="inline-flex items-center gap-2">
              <span>View our projects</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}